import assign from 'object-assign';

export const GACustomEvent = {
    IMPRESSION: 'impression',
    ADD_TO_CART: 'addToCart',
    REMOVE_FROM_CART: 'removeFromCart',
    PRODUCT_VIEW: 'productView',
    PRODUCT_CLICK: 'productClick',
    PROMOTION_CLICK: 'promotionClick',
    PROMOTION_VIEW: 'promotionView'
};
export const GAMeasurementType = {
    IMPRESSIONS: 'impressions',
    CLICK: 'click', // A click on a product or product link for one or more products.
    DETAIL: 'detail', // A view of product details.
    ADD: 'add', // Adding one or more products to a shopping cart.
    REMOVE: 'remove', // Remove one or more products from a shopping cart.
    CHECKOUT: 'checkout', // Initiating the checkout process for one or more products.
    CHECKOUT_OPTION: 'checkout_option', // Sending the option value for a given checkout step.
    PURCHASE: 'purchase', // The sale of one or more products.
    REFUND: 'refund', // The refund of one or more products.
    PROMO_VIEW: 'promoView', // A click on an internal promotion.
    PROMO_CLICK: 'promoClick' // A click on an internal promotion.
};

const googleAnalyticsProto = {
    send( data, callback ) {
        if ( !window.dataLayer ) {
            window.dataLayer = [];
        }

        if ( callback && typeof callback === 'function' ) {
            data.eventCallback = this.handleCallback( callback );
        }

        window.dataLayer.push( data );

        return this;
    },

    /**
     * Handler to ensure our callback gets called regardless of successful response from dataLayer
     * @param callback
     * @returns {function(...[*]=)}
     */
    handleCallback: callback => {
        const timeoutId = setTimeout( callback, 1500 );

        return () => {
            clearTimeout( timeoutId );
            callback();
        };
    },

    createEventData( {event, ecommerceMeasurement, data} ) {
        return {
            event,
            ecommerce: {
                [ecommerceMeasurement]: data
            }
        };
    },

    createProductData( item ) {
        return {
            id: item.sku,
            price: item.customerPrice || item.catalogPrice,
            name: item.kitName,
            brand: item.brandName || item.productName,
            category: item.productFamily,
            quantity: item.quantity || 1
        };
    },

    createPromotionData( {
                             promoId = '',
                             promoName = '',
                             promoCreativeName = '',
                             promoPosition = ''
                         } ) {
        return {
            id: promoId,
            name: promoName,
            creative: promoCreativeName,
            position: promoPosition
        }
    }
};

export const googleAnalyticsLayer = assign( {}, googleAnalyticsProto, {
    crossSellItems( list = [], callback ) {
        const impressions = list.map( this.createProductData );
        const data = this.createEventData( {
            event: GACustomEvent.IMPRESSION,
            ecommerceMeasurement: GAMeasurementType.IMPRESSIONS,
            data: impressions
        } );

        this.send( data, callback );
    },

    itemsAdded( items = [], callback ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }
        const products = items.map( this.createProductData );
        const data = this.createEventData( {
            event: GACustomEvent.ADD_TO_CART,
            ecommerceMeasurement: GAMeasurementType.ADD,
            data: {products}
        } );

        this.send( data, callback );
    },

    itemsRemoved( items = [], callback ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }
        const products = items.map( this.createProductData );
        const data = this.createEventData( {
            event: GACustomEvent.REMOVE_FROM_CART,
            ecommerceMeasurement: GAMeasurementType.REMOVE,
            data: {products}
        } );

        this.send( data, callback );
    },

    productView( item, callback ) {
        const product = this.createProductData( item );
        const data = this.createEventData( {
            event: GACustomEvent.PRODUCT_VIEW,
            ecommerceMeasurement: GAMeasurementType.DETAIL,
            data: {products: [product]}
        } );

        this.send( data, callback );
    },

    promoView( promo, callback ) {
        const promotion = this.createPromotionData( promo );
        const data = this.createEventData( {
            event: GACustomEvent.PROMOTION_VIEW,
            ecommerceMeasurement: GAMeasurementType.PROMO_VIEW,
            data: {promotions: [promotion]}
        } );

        this.send( data, callback );
    },

    promoClick( promo, callback ) {
        const promotion = this.createPromotionData( promo );
        const data = this.createEventData( {
            event: GACustomEvent.PROMOTION_CLICK,
            ecommerceMeasurement: GAMeasurementType.PROMO_CLICK,
            data: {promotions: [promotion]}
        } );

        this.send( data, callback );
    }
} );
